import { useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';
import roomStyles from '../Room.module.css';
import styles from './SkillsRoom.module.css';

type Skill = {
  id: string;
  name: string;
  value: number;
  system: string;
  detail: string;
  x: number;
  y: number;
  mobileX: number;
  mobileY: number;
  compactX: number;
  compactY: number;
};

const skillCartridges: Skill[] = [
  {
    id: 'php',
    name: 'PHP',
    value: 98,
    system: 'SERVER CORE',
    detail: 'APIs · platforms · integrations',
    x: 21.4,
    y: 56,
    mobileX: 24,
    mobileY: 48,
    compactX: 24,
    compactY: 44,
  },
  {
    id: 'mysql',
    name: 'MySQL',
    value: 97,
    system: 'DATA CORE',
    detail: 'schemas · queries · performance',
    x: 51.6,
    y: 56,
    mobileX: 33,
    mobileY: 48,
    compactX: 41,
    compactY: 43,
  },
  {
    id: 'postgres',
    name: 'PostgreSQL',
    value: 96,
    system: 'DATA CORE',
    detail: 'relational systems · search',
    x: 11.9,
    y: 87,
    mobileX: 53,
    mobileY: 48,
    compactX: 61,
    compactY: 45,
  },
  {
    id: 'node',
    name: 'Node.js',
    value: 97,
    system: 'RUNTIME',
    detail: 'services · realtime · tooling',
    x: 21.7,
    y: 80,
    mobileX: 24,
    mobileY: 68,
    compactX: 23,
    compactY: 55,
  },
  {
    id: 'nest',
    name: 'NestJS',
    value: 96,
    system: 'SERVER CORE',
    detail: 'typed architecture · APIs',
    x: 34.3,
    y: 85,
    mobileX: 45,
    mobileY: 69,
    compactX: 43,
    compactY: 59,
  },
  {
    id: 'next',
    name: 'Next.js',
    value: 97,
    system: 'WEB ENGINE',
    detail: 'products · rendering · DX',
    x: 24.8,
    y: 91,
    mobileX: 59,
    mobileY: 68,
    compactX: 65,
    compactY: 54,
  },
  {
    id: 'react',
    name: 'React',
    value: 98,
    system: 'UI ENGINE',
    detail: 'interfaces · state · motion',
    x: 47.8,
    y: 82,
    mobileX: 34,
    mobileY: 75,
    compactX: 34,
    compactY: 61,
  },
  {
    id: 'typescript',
    name: 'TypeScript',
    value: 97,
    system: 'LANGUAGE CORE',
    detail: 'types · tooling · architecture',
    x: 73.2,
    y: 84.5,
    mobileX: 22,
    mobileY: 87,
    compactX: 22,
    compactY: 65,
  },
  {
    id: 'angular',
    name: 'Angular',
    value: 95,
    system: 'UI ENGINE',
    detail: 'enterprise frontends',
    x: 62.2,
    y: 86,
    mobileX: 51,
    mobileY: 77,
    compactX: 45,
    compactY: 63,
  },
  {
    id: 'vue',
    name: 'Vue',
    value: 95,
    system: 'UI ENGINE',
    detail: 'fast reactive interfaces',
    x: 81.1,
    y: 61.3,
    mobileX: 67,
    mobileY: 77,
    compactX: 66,
    compactY: 66,
  },
  {
    id: 'agents',
    name: 'Agentic AI',
    value: 96,
    system: 'AI MODULE',
    detail: 'tools · workflows · autonomy',
    x: 91.5,
    y: 63.4,
    mobileX: 70,
    mobileY: 84,
    compactX: 30,
    compactY: 69,
  },
  {
    id: 'rag',
    name: 'RAG',
    value: 85,
    system: 'AI MODULE',
    detail: 'retrieval · context · grounding',
    x: 83.7,
    y: 69,
    mobileX: 33,
    mobileY: 86,
    compactX: 52,
    compactY: 69,
  },
  {
    id: 'go',
    name: 'Go',
    value: 35,
    system: 'SIDE QUEST',
    detail: 'learning · experimenting',
    x: 92.8,
    y: 73.1,
    mobileX: 51,
    mobileY: 89,
    compactX: 70,
    compactY: 68,
  },
];

const organizerSlots = [
  { x: 250, y: 118 },
  { x: 500, y: 118 },
  { x: 750, y: 118 },
  { x: 1000, y: 118 },
  { x: 1250, y: 118 },
  { x: 295, y: 266 },
  { x: 600, y: 266 },
  { x: 905, y: 266 },
  { x: 1215, y: 266 },
  { x: 225, y: 420 },
  { x: 575, y: 420 },
  { x: 905, y: 420 },
  { x: 1250, y: 420 },
] as const;

type LoadStage = 'idle' | 'inserting' | 'loading' | 'ready';

type SkillsRoomProps = {
  sectionRef: RefObject<HTMLElement | null>;
  showIdleHint: boolean;
};

export function SkillsRoom({ sectionRef, showIdleHint }: SkillsRoomProps) {
  const loadTimersRef = useRef<number[]>([]);
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [loadStage, setLoadStage] = useState<LoadStage>('idle');
  const [organizerFocused, setOrganizerFocused] = useState(false);

  useEffect(() => () => loadTimersRef.current.forEach(window.clearTimeout), []);

  const selectSkill = (skill: Skill) => {
    loadTimersRef.current.forEach(window.clearTimeout);
    loadTimersRef.current = [];
    setOrganizerFocused(false);
    setSelectedSkill(skill);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setLoadStage('ready');
      return;
    }
    setLoadStage('inserting');
    loadTimersRef.current.push(window.setTimeout(() => setLoadStage('loading'), 620));
    loadTimersRef.current.push(window.setTimeout(() => setLoadStage('ready'), 1780));
  };

  return (
    <section
      ref={sectionRef}
      className={`${roomStyles.room} ${roomStyles.wideRoom} ${styles.room}`}
      aria-label="Interactive pixel-art skill room"
    >
      <div
        className={`${roomStyles.scene} ${styles.scene} ${organizerFocused ? styles.sceneFocused : ''}`}
        onClick={() => setOrganizerFocused(false)}
      >
        <picture className={roomStyles.background}>
          <source media="(max-width: 680px)" srcSet="/assets/v5-room-skills-mobile-clean-v2.png" />
          <source media="(min-width: 681px)" srcSet="/assets/v5-room-skills-ultrawide.png" />
          <img
            src="/assets/v5-room-skills-clean-v2.png"
            alt="Pixel-art bedroom with a large CRT television, low media cabinet and bed"
          />
        </picture>

        <div className={styles.consoleWindow} data-stage={loadStage} aria-hidden="true">
          {[0, 1, 2, 3, 4].map((frame) => (
            <span key={frame} className={styles.consoleFrame} data-frame={frame}>
              <img src={`/assets/v5-skill-console-frame-${frame}.png`} alt="" />
            </span>
          ))}
        </div>

        <div
          className={`${styles.skillScreen} ${loadStage !== 'idle' ? styles.skillScreenOn : ''}`}
          aria-live="polite"
        >
          {loadStage === 'idle' ? (
            <div className={styles.emptyScreen}>
              <span>SKILL QUEST v1.0</span>
              <b>
                INSERT
                <br />A GAME
              </b>
              <small>SELECT A CARTRIDGE ↓</small>
            </div>
          ) : (
            <div className={styles.loadedScreen}>
              <span>
                {loadStage === 'inserting'
                  ? 'READING CART...'
                  : loadStage === 'loading'
                    ? 'LOADING GAME...'
                    : 'GAME READY'}
              </span>
              <b>{selectedSkill?.name}</b>
              <small>{selectedSkill?.system}</small>
              <div className={styles.skillMeter}>
                <i style={{ '--skill-value': `${selectedSkill?.value ?? 0}%` } as CSSProperties} />
              </div>
              <strong>
                {loadStage === 'ready' ? `${selectedSkill?.value}% MASTERY` : 'PLEASE WAIT_'}
              </strong>
              <em>{loadStage === 'ready' ? selectedSkill?.detail : 'VERIFYING EXPERIENCE DATA'}</em>
            </div>
          )}
          {showIdleHint && (
            <output className={styles.idlePrompt}>
              <span>CONTINUE EXPLORING</span>
              <b>
                SCROLL ↓ OR
                <br />
                PICK A CARTRIDGE
              </b>
            </output>
          )}
        </div>

        <div className={styles.organizerStage}>
          <button
            type="button"
            className={styles.organizer}
            onClick={(event) => {
              event.stopPropagation();
              setOrganizerFocused(true);
            }}
            aria-label="Zoom in on the skill cartridge organizer"
            aria-pressed={organizerFocused}
          >
            <img src="/assets/v5-skill-organizer-v7-cropped.png" alt="" />
          </button>

          <div className={styles.cartridgeField} aria-label="Skill game cartridges">
            {skillCartridges.map((skill, index) => {
              const active = selectedSkill?.id === skill.id;
              const slot = organizerSlots[index];
              const slotX = (slot.x / 1500) * 100;
              const slotY = (slot.y / 640) * 100;
              const desktopX = 17.42 + (slot.x / 1500) * 31.25;
              const desktopY = 44.373 + (slot.y / 640) * 13.333;
              const mobileX = 6.17 + (slot.x / 1500) * 87.9;
              const mobileY = 96.3 + (slot.y / 640) * 37.504;
              const compactY = 79.2 + (slot.y / 640) * 37.504;
              const diskStyle = {
                '--sprite': `url("/assets/v5-cart-${skill.id}.png")`,
                '--slot-x': `${slotX}%`,
                '--slot-y': `${slotY}%`,
                '--dfly-x': `${33.8 - desktopX}cqw`,
                '--dfly-y': `${31 - desktopY}cqw`,
                '--mfly-x': `${39 - mobileX}cqw`,
                '--mfly-y': `${72 - mobileY}cqw`,
                '--cfly-x': `${39 - mobileX}cqw`,
                '--cfly-y': `${72 - compactY}cqw`,
              } as CSSProperties;
              return (
                <button
                  key={skill.id}
                  type="button"
                  className={`${styles.cartridge} ${active ? styles.cartridgeActive : ''}`}
                  style={diskStyle}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(event) => {
                    event.stopPropagation();
                    selectSkill(skill);
                  }}
                  aria-label={`Load ${skill.name} game cartridge, mastery ${skill.value} percent`}
                  aria-pressed={active}
                />
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
